import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Users, Plus, Trash2, Sparkles, ExternalLink } from "lucide-react";

export default function Connections() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", company: "", role: "", linkedin_url: "" });
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/connections");
      setItems(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company) return;
    await api.post("/connections", form);
    setForm({ name: "", company: "", role: "", linkedin_url: "" });
    load();
  };

  const remove = async (cid) => {
    await api.delete(`/connections/${cid}`);
    load();
  };

  const seedDemo = async () => {
    setSeeding(true);
    try {
      await api.post("/connections/seed-demo");
      load();
    } finally { setSeeding(false); }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="connections-page">
      <div className="jr-card" style={{ background: "linear-gradient(135deg, rgba(34,211,238,0.15), rgba(139,92,246,0.08))" }}>
        <div className="flex items-center gap-2 mb-2">
          <Users className="w-5 h-5 text-cyan-300" />
          <div className="font-bold text-white text-lg">Your Warm-Intro Network</div>
          <span className="jr-pill bg-cyan-500/20 text-cyan-200 text-[10px]">NEW</span>
        </div>
        <p className="text-sm text-zinc-300 mb-4">
          Add people you know at companies you're interested in. When Curcompass finds a job at one of those companies, you'll see a
          ✨ <strong className="text-cyan-200">Warm Intro</strong> button — Claude will draft a referral request you can send directly.
          <span className="text-zinc-400 block mt-1">Referrals reply 4× more often than cold applications.</span>
        </p>
        <button onClick={seedDemo} disabled={seeding} className="jr-btn-ghost text-sm" data-testid="seed-demo-btn">
          <Sparkles className="w-4 h-4 inline mr-1" />
          {seeding ? "Seeding..." : "Try with demo connections"}
        </button>
      </div>

      <form onSubmit={add} className="jr-card grid md:grid-cols-5 gap-2" data-testid="add-connection-form">
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name *" className="jr-input md:col-span-1" data-testid="conn-name" required />
        <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Company *" className="jr-input md:col-span-1" data-testid="conn-company" required />
        <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Role" className="jr-input md:col-span-1" data-testid="conn-role" />
        <input value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} placeholder="LinkedIn URL" className="jr-input md:col-span-1" data-testid="conn-linkedin" />
        <button type="submit" className="jr-btn-primary md:col-span-1" data-testid="add-connection-btn">
          <Plus className="w-4 h-4 inline mr-1" /> Add
        </button>
      </form>

      <div className="jr-card">
        <div className="text-sm text-zinc-400 mb-3">Total connections: <span className="text-white font-semibold">{items.length}</span></div>
        {loading && <div className="text-zinc-500 text-sm">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="text-center text-zinc-500 text-sm py-8">No connections yet. Add some above or click &quot;Try with demo connections&quot; to test the flow.</div>
        )}
        <div className="space-y-2">
          {items.map((c) => (
            <div key={c.connection_id} className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.03]" data-testid="connection-row">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-violet-500 grid place-items-center text-white font-bold text-sm">
                {c.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-white">{c.name}</div>
                <div className="text-xs text-zinc-400">{c.role} @ {c.company}</div>
              </div>
              {c.linkedin_url && (
                <a href={c.linkedin_url} target="_blank" rel="noreferrer" className="jr-btn-ghost p-2" title="Open LinkedIn">
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
              <button onClick={() => remove(c.connection_id)} className="jr-btn-ghost p-2 text-rose-300 hover:text-rose-200" data-testid="delete-connection-btn">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
